/**
 * @file SpendByProductReport.tsx
 * @brief Company report — spend / invoices by billable product.
 *
 * @references
 * - GET /api/company/reports/spend-by-product
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { FileText, Wallet } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import { ProductLabelCell } from "@/features/reports-shared/productIcons";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { groupRowsByCurrency } from "@/shared/utils/reportCurrency";
import { useSpendByProductReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
  sumInCurrency,
} from "@/features/reports-shared/previousRange";
import { ReportShell } from "./ReportShell";

export function SpendByProductReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useSpendByProductReport(queryRange);
  // The same-length window before, for the +/-% pills (03-10-2026).
  const prevQueryRange = useMemo(() => previousRange(queryRange), [queryRange]);
  const prevReport = useSpendByProductReport(prevQueryRange);
  const compareLabel = previousRangeLabel(queryRange);
  // Memoized so the currency grouping below is not rebuilt on every
  // render (the `?? []` literal is a fresh array each time).
  const rows = useMemo(() => report.data ?? [], [report.data]);
  // (24-08-2026) The "Cost" KPI and column that used to sit here were
  // `session_tokens.total_cost_cents` — Verblizr's AI vendor COGS — rendered on
  // the CUSTOMER's own reports page, next to Spend, which made the gross margin
  // on their account readable off the screen (and out of the exported CSV).
  // The endpoint no longer returns cost or profit at all.
  //
  // Rows arrive per (product, currency). Render the primary currency's rows and
  // name the others rather than adding pennies to cents.
  const byCurrency = useMemo(
    () => groupRowsByCurrency(rows, (r) => Number(r.spend_cents || 0)),
    [rows],
  );
  const visibleRows = byCurrency.primaryRows;
  const currency = byCurrency.primary;
  const totals = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => ({
          invoice_count: acc.invoice_count + Number(row.invoice_count || 0),
          spend_cents: acc.spend_cents + Number(row.spend_cents || 0),
        }),
        { invoice_count: 0, spend_cents: 0 },
      ),
    [visibleRows],
  );

  const prevRows = prevReport.data;
  const spendDelta = prevRows
    ? percentChange(
        totals.spend_cents,
        sumInCurrency(prevRows, currency, (r) => Number(r.spend_cents || 0)),
      )
    : undefined;
  const invoicesDelta = prevRows
    ? percentChange(
        totals.invoice_count,
        sumInCurrency(prevRows, currency, (r) => Number(r.invoice_count || 0)),
      )
    : undefined;

  return (
    <ReportShell
      title="Spend by product"
      description="What your company was billed, on the same basis as the invoice feed."
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "spend-by-product",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Spend"
              icon={Wallet}
              value={formatMoneyMinor(totals.spend_cents, currency)}
              hint={
                byCurrency.mixed
                  ? `${currency} only; also billed in ${byCurrency.groups
                      .slice(1)
                      .map((g) => g.currency)
                      .join(", ")}`
                  : compareLabel
              }
              delta={spendDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Invoices"
              icon={FileText}
              value={String(totals.invoice_count)}
              hint={compareLabel}
              delta={invoicesDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Product spend"
          description="Billed amounts per product. Item counts can differ from the Invoices page, which lists only rows that produced an invoice."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Product",
              { label: "Invoices", align: "right" },
              { label: "Spend", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={visibleRows.length === 0}
            footer={
              visibleRows.length > 0 ? (
                <tr>
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {totals.invoice_count}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMoneyMinor(totals.spend_cents, currency)}
                  </td>
                </tr>
              ) : null
            }
          >
            {visibleRows.map((row) => (
              <tr key={row.product} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <ProductLabelCell
                    product={row.product}
                    label={row.product_label}
                  />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.invoice_count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.spend_cents, currency)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
